import { useState, useEffect, useCallback, Fragment, createContext, useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { connect, useSelector, useDispatch } from 'react-redux'
import { FeatureFlag } from 'Optimizely'
import { gigHistorySelector } from 'selectors'
import { fetchDrivingSummary } from 'actions/driving'
import { Link } from 'react-router-dom'
import Form from 'components/form'
import Input from 'components/form/Input'
import Page from 'rui/widgets/Page'
import Panel, { PanelSpacer, PanelTitle, PanelPadding } from 'components/layout/Panel'
import DriveSidebar from 'components/layout/DriveSidebar'
import styled, { css } from 'styled-components/macro'
import media from 'styles/mixins/media'
import { body1, body2, h5, h2 } from 'styles/mixins/textstyles'
import { Caption, H6, H4, Body1, Overline } from 'components/layout/text'
import { BasicButton } from 'components/layout/buttons'
import { Container, Time, Icon as RoadieUIIcon, Text } from 'roadie-ui'
import { formatNum, capitalize } from 'util/format'
import groupBy from 'lodash/groupBy'
import values from 'lodash/values'
import partition from 'lodash/partition'
import uniq from 'lodash/uniq'
import { api } from 'roadie-ui'
import Icon from 'components/misc/Icon'
//TODO: convert legacy icons over to RoadieUI
import Loader from 'components/misc/Loader'
import Spacer from 'components/misc/Spacer'
import { Tooltip } from 'roadie-ui'
import { getLatestAccountSetting } from 'util/profile'
import { getGoogleTranslateTargetLanguage, translateText } from 'util/translate'
import { fetchBlockPaymentDataForDriver, transformBlockPaymentsToTransactions } from 'data/blockPayments'
import * as mainApi from 'util/server/mainApi'
import {
  getEnglishTextMap,
  createLocalizedTranslations,
  hasLocalMessagesForLanguagePreference,
  LocalizedRichText,
  useLocalizedTranslations,
  useLocalization,
  LocalizedText,
} from 'localization'

const GigHistoryTextKeys = {
  Summary: 'dweb_gig_history_summary',
  AllTimeLabel: 'dweb_gig_history_all_time_label',
  EarningsSummary: 'dweb_gig_history_earnings_summary',
  TotalEarnings: 'dweb_common_total_earnings',
  Deliveries: 'dweb_common_deliveries',
  Miles: 'dweb_gig_history_miles',
  Completed: 'dweb_common_completed',
  Driven: 'dweb_gig_history_driven',
  EarningsDisclaimer: 'dweb_gig_history_earnings_disclaimer',
  TaxInformation: 'dweb_common_tax_information',
  SeattleDashboard: 'dweb_gig_history_seattle_dashboard',
  EarningsStatements: 'dweb_common_earnings_statements',
  VIEW: 'dweb_gig_history_view',
  Canceled: 'dweb_common_canceled',
  AddOn: 'dweb_common_add_on',
  Balance: 'dweb_gig_history_balance',
  CurrentBalance: 'dweb_gig_history_current_balance',
  AutomaticPayment: 'dweb_gig_history_automatic_payment',
  GigPay: 'dweb_common_gig_pay',
  BlockTimeRangeOnDate: 'dweb_gig_history_block_time_range_on_date',
  Expenses: 'dweb_gig_history_expenses',
  Tips: 'dweb_gig_history_tips',
  Instant: 'dweb_gig_history_instant',
  Automatic: 'dweb_gig_history_automatic',
  Payment: 'dweb_gig_history_payment',
  PendingCreditCaption: 'dweb_gig_history_pending_credit_caption',
  Fee: 'dweb_gig_history_fee',
  PaidTo: 'dweb_gig_history_paid_to',
  Processing: 'dweb_gig_history_processing',
  ProcessingCaptionTrusted: 'dweb_gig_history_processing_caption_trusted',
  ProcessingCaptionNotTrusted: 'dweb_gig_history_processing_caption_not_trusted',
  PaymentErrorCaption: 'dweb_gig_history_payment_error_caption',
  ReturnedTransactionsCaption: 'dweb_gig_history_returned_transactions_caption',
  Submitted: 'dweb_common_submitted',
  ShowMore: 'dweb_common_show_more',
  ShowAll: 'dweb_gig_history_show_all',
  AccountCredit: 'dweb_gig_history_account_credit',
  PendingAccountCredit: 'dweb_gig_history_pending_account_credit',
  BlockPayAdjustment: 'dweb_gig_history_block_pay_adjustment',
  ReconciliationAdjustment: 'dweb_gig_history_reconciliation_adjustment',
  CaliforniaEarningsAdjustment: 'dweb_gig_history_california_earnings_adjustment',
  SeattlePremiumPay: 'dweb_gig_history_seattle_premium_pay',
  SeattlePaidSickSafeTime: 'dweb_gig_history_seattle_paid_sick_safe_time',
  NYCGroceryDeliveryAdjustment: 'dweb_gig_history_nycgrocery_delivery_adjustment',
  DeliveryDetailsUnavailableTitle: 'dweb_gig_history_delivery_details_unavailable_title',
  DeliveryDetailsUnavailableDescription: 'dweb_gig_history_delivery_details_unavailable_description',
  GuaranteedMinimumTip: 'dweb_gig_history_guaranteed_minimum_tip',
  TipFromSender: 'dweb_delivery_details_tip_from_sender',
  TipFromRecipient: 'dweb_delivery_details_tip_from_recipient',
  AdditionalTipFromRecipient: 'dweb_gig_history_additional_tip_from_recipient',
  Pending: 'dweb_delivery_details_pending',
}

const GigHistoryStaticText = createLocalizedTranslations(GigHistoryTextKeys, getEnglishTextMap(GigHistoryTextKeys))
const GigHistoryTextContext = createContext(GigHistoryStaticText)

const useGigHistoryText = () => useContext(GigHistoryTextContext)

const FFLink = (props) => {
  return (
    <FeatureFlag feature="drive_gig_details">
      {(enabled) => {
        if (enabled && props.isLink) {
          return <Link {...props} />
        } else {
          return <div className={props.className}>{props.children}</div>
        }
      }}
    </FeatureFlag>
  )
}

function categorizeTransactions(transactions) {
  const categories = {
    processing: [],
    balance: [],
    submitted: [],
    returned: [],
  }

  transactions.forEach((transaction) => {
    // Handle block payments
    if (transaction.block_id) {
      const paymentStatus = transaction.status
      const blockPayment = {
        ...transaction,
        block_details: {
          block_scheduled_start_time: transaction.block_scheduled_start_time,
          block_scheduled_end_time: transaction.block_scheduled_end_time,
          driver_start_time: transaction.driver_start_time,
          driver_end_time: transaction.driver_end_time,
        },
      }

      if (paymentStatus === 'held') {
        categories.processing.push(blockPayment)
      } else if (paymentStatus === 'initialized') {
        categories.balance.push(blockPayment)
      } else if (paymentStatus === 'settled' || paymentStatus === 'submitted') {
        categories.submitted.push(blockPayment)
      } else if (paymentStatus === 'returned') {
        categories.returned.push(blockPayment)
      }
      return
    }

    // Handle regular transactions
    if (transaction.status === 'held') {
      categories.processing.push(transaction)
    } else if (transaction.status === 'initialized') {
      categories.balance.push(transaction)
    } else if (transaction.status === 'settled' || transaction.status === 'submitted') {
      categories.submitted.push(transaction)
    } else if (transaction.status === 'returned') {
      categories.returned.push(transaction)
    }
  })

  return categories
}

function transactionCategorySummaryField(transaction) {
  switch (transaction.category) {
    case 'base_payout':
    case 'cancellation_payout':
    case 'roadies_cut_adjustment':
    case 'seattle_minimum_pay_adjustment':
    case null:
      return 'gig_pay'

    case 'promotional_credit':
    case 'reconciliation':
    case 'prop22_earnings_adjustment':
    case 'seattle_premium':
    case 'seattle_psst':
    case 'nyc_earnings_adjustment':
    case 'pending_credit':
      return transaction.category

    case 'expense_adjustment':
    case 'toll_adjustment':
    case 'reimbursement':
      return 'reimbursement'

    case 'guaranteed_tip':
    case 'tip':
    case 'net_tip':
    case 'incidentals':
      return 'tip'

    case 'instant_pay_fee':
      return 'fees'

    case 'expense':
    case 'estimated_tolls':
      return null

    default:
      return undefined
  }
}

function hasGuaranteedTip(transactions) {
  return Boolean(transactions.find((t) => t.category === 'guaranteed_tip'))
}

function isProcessingTransaction(transaction) {
  return transaction.status === 'held'
}

function transactionTipTypeLabel(transaction, hasGmt = false, staticTextMap = GigHistoryStaticText) {
  const pendingPrefix = isProcessingTransaction(transaction) ? `${staticTextMap.Pending} ` : ''
  switch (transaction.category) {
    case 'guaranteed_tip':
      return staticTextMap.GuaranteedMinimumTip
    case 'incidentals':
      return staticTextMap.TipFromSender
    case 'net_tip':
      return hasGmt ? staticTextMap.AdditionalTipFromRecipient : staticTextMap.TipFromRecipient
    default:
      return hasGmt
        ? `${pendingPrefix}${staticTextMap.AdditionalTipFromRecipient}`
        : `${pendingPrefix}${staticTextMap.TipFromRecipient}`
  }
}

function transactionPayAfterCopy(transaction) {
  const payAfter = Time.fromDate(new Date(transaction.pay_after))
  const today = new Time()

  if (today.date.getTime() > payAfter.date.getTime()) {
    return 'Awaiting confirmation from Sender'
  }

  const dateStr = payAfter.toString()
  return `Tip can be updated until ${dateStr}`
}

function transactionSums(transactions) {
  const summaries = {
    total: 0,
  }

  transactions.forEach((transaction) => {
    let cat = transactionCategorySummaryField(transaction)

    if (
      cat === 'promotional_credit' ||
      cat === 'reconciliation' ||
      cat === 'prop22_earnings_adjustment' ||
      cat === 'seattle_minimum_pay_adjustment' ||
      cat === 'seattle_premium' ||
      cat === 'seattle_psst' ||
      cat === 'nyc_earnings_adjustment'
    ) {
      cat = 'gig_pay'
    }

    if (!cat) {
      return
    }
    if (!summaries[cat]) {
      summaries[cat] = 0
    }
    summaries[cat] += transaction.amount
    summaries.total += transaction.amount
  })

  return summaries
}

function combineTrans(transactions, category) {
  const [matching, nonMatching] = partition(transactions, (transaction) => {
    return transaction.category === category
  })

  const grouped = groupBy(matching, 'consolidation_id')

  const combined = values(grouped).map((trans) => {
    const main = trans[0]
    main.amount = trans.reduce((sum, next) => sum + next.amount, 0)
    return main
  })

  return [...combined, ...nonMatching]
}

function combinePayoutGigPayTransactions(transactions) {
  const [gigPayTransactions, nonGigPayTransactions] = partition(transactions, (transaction) => {
    return transactionCategorySummaryField(transaction) === 'gig_pay'
  })

  const gigPayTransactionsByConsolidationOrBlock = groupBy(gigPayTransactions, (transaction) => {
    // For block payments, group by block_id
    if (transaction.block_id) {
      return transaction.block_id
    }
    // For regular transactions, group by consolidation_id
    return transaction.consolidation_id
  })

  const combinedGigPayTransactions = values(gigPayTransactionsByConsolidationOrBlock).map((trans) => {
    const main =
      trans.find((t) => t.category === 'base_payout') ||
      trans.find((t) => t.category === 'cancellation_payout') ||
      trans.find((t) => t.category === 'seattle_minimum_pay_adjustment') ||
      trans[0]

    main.amount = trans.reduce((sum, next) => sum + next.amount, 0)

    return main
  })

  return [...combinedGigPayTransactions, ...combineTrans(nonGigPayTransactions, 'guaranteed_tip')]
}

function groupTransactionsIntoPayouts(transactions) {
  const transactionByPayoutId = values(groupBy(transactions, 'payout_id'))
  const payouts = []
  for (let group of transactionByPayoutId) {
    const payout = {
      payout_id: group[0].payout_id,
      summaries: transactionSums(group),
      gig_ids: Array.from(new Set(group.map((g) => g.gig_id))),
      consolidation_ids: Array.from(new Set(group.filter((g) => !!g.consolidation_id).map((g) => g.consolidation_id))),
      transactions: combinePayoutGigPayTransactions(group),
      created_at: group[0].created_at,
      updated_at: group[0].updated_at,
      masked_account_number: group[0].masked_account_number,
      account_type: group[0].account_type,
    }
    payouts.push(payout)
  }
  return payouts
}

async function fetchPayoutGroups(page = 1, profile_id) {
  let regularTransactions = []
  let blockPaymentTransactions = []
  let allBlockGigIds = []

  // TODO: Refactor these fetch calls to be done in parallel (instead of sequentially)
  const regularTransactionResponse = await mainApi.getTransactions(page)
  if (regularTransactionResponse.data) {
    regularTransactions = regularTransactionResponse.data.payment_transactions.filter((t) => t.category !== 'tip')
    regularTransactions.forEach((t) => {
      if (!t.consolidation_id && t.gig_id) {
        t.consolidation_id = `gig:${t.gig_id}`
      }
    })
  } else {
    console.log('Error fetching regular transactions:', regularTransactionResponse.error)
  }

  try {
    const blockPaymentResponse = await fetchBlockPaymentDataForDriver(profile_id, page, 5)

    if (blockPaymentResponse?.data && Array.isArray(blockPaymentResponse.data)) {
      blockPaymentResponse.data.forEach((blockPaymentData) => {
        allBlockGigIds.push(...(blockPaymentData.gigIds || []))

        // Find a matching regular transaction to get the masked_account_number
        const transactionForBlockPayment = regularTransactions.find((t) => blockPaymentData.gigIds.includes(t.gig_id))
        const maskedAccountNumber = transactionForBlockPayment?.masked_account_number || ''

        if (blockPaymentData.payments) {
          blockPaymentTransactions.push(
            ...transformBlockPaymentsToTransactions(
              blockPaymentData.payments.held,
              'held',
              blockPaymentData,
              maskedAccountNumber
            )
          )
          blockPaymentTransactions.push(
            ...transformBlockPaymentsToTransactions(
              blockPaymentData.payments.initialized,
              'initialized',
              blockPaymentData,
              maskedAccountNumber
            )
          )
          blockPaymentTransactions.push(
            ...transformBlockPaymentsToTransactions(
              blockPaymentData.payments.settled,
              'settled',
              blockPaymentData,
              maskedAccountNumber
            )
          )
          blockPaymentTransactions.push(
            ...transformBlockPaymentsToTransactions(
              blockPaymentData.payments.submitted,
              'submitted',
              blockPaymentData,
              maskedAccountNumber
            )
          )
          blockPaymentTransactions.push(
            ...transformBlockPaymentsToTransactions(
              blockPaymentData.payments.returned,
              'returned',
              blockPaymentData,
              maskedAccountNumber
            )
          )
        }
      })
    }
  } catch (error) {
    console.log('Error fetching block payments:', error)
  }

  // Filter out regular transactions that are included in block payments
  const filteredRegularTransactions = regularTransactions.filter((t) => !allBlockGigIds.includes(t.gig_id))

  // Combine filtered regular transactions with block payments
  const allTransactions = [...filteredRegularTransactions, ...blockPaymentTransactions]

  const transactionsByCategories = categorizeTransactions(allTransactions)
  const payoutGroups = {}
  for (let category in transactionsByCategories) {
    payoutGroups[category] = groupTransactionsIntoPayouts(transactionsByCategories[category])
  }

  return payoutGroups
}

async function fetchConsolidations(ids) {
  return api.get(`/consolidations/details?ids=${ids.filter((x) => x).join(',')}`).then((resp) => {
    return resp.data.consolidations
  })
}

const TransactionDate = styled.div`
  text-transform: uppercase;
  text-align: center;
  width: 3.2rem;
  margin-right: 3.2rem;
`
const TransactionDetails = styled.div`
  flex: 1 1 100%;
  word-break: break-word;
`
const TransactionAmount = styled.div`
  margin-left: 3.2rem;

  span {
    vertical-align: 0.8rem;
    font-size: 1.8rem;
  }
`

const TransactionWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 2.4rem;
  position: relative;
  cursor: ${(props) => (props.pointer ? 'pointer' : 'default')};
`

const TransactionLinkWrapper = styled(FFLink)`
  display: flex;
  align-items: center;
  padding: 2.4rem;
  position: relative;
`

const TransactionReturnIndicator = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0.6rem;
  background: ${(props) => props.theme.colors.errorLight};
`

const PaymentError = styled.div`
  background: ${(props) => props.theme.colors.errorLight};
  padding: 2rem;
  border-radius: 5px;

  a {
    color: ${(props) => props.theme.colors.greyDarker};
  }
`

const NoBreak = styled.span`
  white-space: nowrap;
`

const SenderBadge = styled.div`
  display: flex;
`

const SenderIcon = styled.img`
  height: 1.6rem;
  object-fit: scale-down;
`

const SenderIconWrapper = styled.div`
  margin-right: 0.8rem;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  overflow: hidden;
`

const SummaryAmount = (props) => {
  const { amount = 0, ...rest } = props
  return <Body1 {...rest}>${amount.toFixed(2)}</Body1>
}

const transactionCategoryLabel = (category, staticTextMap, transaction = null) => {
  switch (category) {
    case 'promotional_credit':
      return staticTextMap.AccountCredit
    case 'pending_credit':
      return staticTextMap.PendingAccountCredit
    case 'reconciliation':
      return transaction?.block_id ? staticTextMap.BlockPayAdjustment : staticTextMap.ReconciliationAdjustment
    case 'prop22_earnings_adjustment':
      return staticTextMap.CaliforniaEarningsAdjustment
    case 'seattle_premium':
      return staticTextMap.SeattlePremiumPay
    case 'seattle_psst':
      return staticTextMap.SeattlePaidSickSafeTime
    case 'nyc_earnings_adjustment':
      return staticTextMap.NYCGroceryDeliveryAdjustment
    default:
      return null
  }
}

const formatBlockPaymentCaption = (startTime, endTime, staticTextMap, toLocalizedTime) => {
  const startTimeObj = toLocalizedTime(startTime)
  const endTimeObj = toLocalizedTime(endTime)

  return staticTextMap.text('BlockTimeRangeOnDate', {
    startTime: startTimeObj.getTimeString(),
    endTime: endTimeObj.getTimeString(),
    date: startTimeObj.getDateString(),
  })
}

const TransactionSection = (props) => {
  const { consolidation, transaction } = props
  const staticTextMap = useGigHistoryText()
  const { toLocalizedTime } = useLocalization()
  const categorySummaryField = transactionCategorySummaryField(transaction)

  const transactionTextMap = {
    completed: staticTextMap.Completed || 'Completed',
    canceled: staticTextMap.Canceled || 'Canceled',
    add_on: staticTextMap.AddOn || 'Add-On',
  }

  const isPOTO = consolidation?.offer?.source === 'poto'

  switch (categorySummaryField) {
    case 'reimbursement':
      return (
        <TransactionDetails>
          <H6 id={`transaction-details-header-${transaction.id}`} className="transaction-details-header">
            <LocalizedText translationKey="dweb_gig_history_reimbursement_from_sender" />
          </H6>
          <div>
            {transaction.block_id ? (
              <Caption inline emphasis="medium">
                {formatBlockPaymentCaption(
                  transaction.block_scheduled_start_time,
                  transaction.block_scheduled_end_time,
                  staticTextMap,
                  toLocalizedTime
                )}
              </Caption>
            ) : (
              <Caption inline emphasis="medium">
                {consolidation.title}
              </Caption>
            )}
          </div>
        </TransactionDetails>
      )

    case 'tip':
      return (
        <TransactionDetails>
          <H6 id={`transaction-details-header-${transaction.id}`} className="transaction-details-header">
            {transactionTipTypeLabel(transaction, !!consolidation?.payout_breakdown.guaranteed_tip, staticTextMap)}
          </H6>
          <div>
            {transaction.block_id ? (
              <Caption inline emphasis="medium">
                {formatBlockPaymentCaption(
                  transaction.block_scheduled_start_time,
                  transaction.block_scheduled_end_time,
                  staticTextMap,
                  toLocalizedTime
                )}
              </Caption>
            ) : (
              <Caption inline emphasis="medium">
                {consolidation.title}
              </Caption>
            )}
          </div>
          {isProcessingTransaction(transaction) && transaction.pay_after ? (
            <div>
              <Caption inline emphasis="low">
                {transactionPayAfterCopy(transaction)}
              </Caption>
            </div>
          ) : null}
        </TransactionDetails>
      )

    case 'pending_credit':
      return (
        <TransactionDetails>
          <H6 id={`transaction-details-header-${transaction.id}`} className="transaction-details-header">
            {transactionCategoryLabel(categorySummaryField, staticTextMap)}
          </H6>
          <Caption emphasis="medium">{staticTextMap.PendingCreditCaption}</Caption>
        </TransactionDetails>
      )

    case 'promotional_credit':
    case 'reconciliation':
    case 'prop22_earnings_adjustment':
    case 'seattle_premium':
    case 'seattle_psst':
    case 'nyc_earnings_adjustment':
      return (
        <TransactionDetails>
          <H6 id={`transaction-details-header-${transaction.id}`} className="transaction-details-header">
            {transactionCategoryLabel(categorySummaryField, staticTextMap, transaction)}
          </H6>
          {transaction.block_id && (
            <Caption inline emphasis="medium">
              {formatBlockPaymentCaption(
                transaction.block_scheduled_start_time,
                transaction.block_scheduled_end_time,
                staticTextMap,
                toLocalizedTime
              )}
            </Caption>
          )}
        </TransactionDetails>
      )

    default: {
      // Handle block payments
      if (transaction.block_id) {
        const { block_details } = transaction
        const blockStartTime = toLocalizedTime(block_details.block_scheduled_start_time)
        const blockEndTime = toLocalizedTime(block_details.block_scheduled_end_time)
        const driverStartTime = toLocalizedTime(block_details.driver_start_time)
        const driverEndTime = toLocalizedTime(block_details.driver_end_time)

        return (
          <TransactionDetails>
            <H6>
              <LocalizedRichText
                translationKey="dweb_gig_history_block_value1"
                params={{
                  value1:
                    block_details.block_scheduled_start_time && block_details.block_scheduled_end_time
                      ? ` ${blockStartTime.getTimeString()} - ${blockEndTime.getTimeString()}`
                      : '',
                }}
              />
            </H6>
            {block_details.driver_start_time && block_details.driver_end_time && (
              <Caption inline emphasis="medium">
                <LocalizedRichText
                  translationKey="dweb_gig_history_duration_value1_value2"
                  params={{ value1: driverStartTime.getTimeString(), value2: driverEndTime.getTimeString() }}
                />
              </Caption>
            )}
          </TransactionDetails>
        )
      }

      if (!consolidation) {
        return (
          <TransactionDetails>
            <H6 id={`transaction-details-header-${transaction.id}`} className="transaction-details-header">
              {staticTextMap.DeliveryDetailsUnavailableTitle}
            </H6>
            <Caption inline emphasis="medium">
              {staticTextMap.DeliveryDetailsUnavailableDescription}
            </Caption>
          </TransactionDetails>
        )
      }

      // Handle regular transactions
      const stops = consolidation.stops.sort((a, b) => a.index > b.index)
      const supportIds = uniq(stops.reduce((ids, stop) => [...ids, ...stop.support_ids], []))
        .filter((id) => id)
        .map((id) => `#${id}`)
      const deliveryTimestamp = consolidation.delivery_completed_at || consolidation.canceled_at
      return (
        <TransactionDetails>
          <FeatureFlag feature="drive_gig_details">
            {(enabled) =>
              enabled ? (
                <>
                  {consolidation.senders && consolidation.senders.length ? (
                    <SenderBadge>
                      {consolidation.senders.map((sender) => (
                        <Fragment key={sender.id}>
                          <SenderIconWrapper>
                            <SenderIcon src={sender.avatar_url} />
                          </SenderIconWrapper>{' '}
                          <Overline emphasis="medium">{sender.name}</Overline>
                        </Fragment>
                      ))}
                    </SenderBadge>
                  ) : null}
                  <H6>
                    {isPOTO && transactionTextMap.add_on + ': '}
                    {consolidation.title}
                  </H6>
                  {deliveryTimestamp ? (
                    <Caption inline emphasis="medium">
                      <LocalizedRichText
                        translationKey="dweb_gig_history_value1_at_value2"
                        params={{
                          value1: capitalize(transactionTextMap[consolidation.state]),
                          value2: toLocalizedTime(deliveryTimestamp * 1000).getTimeString(),
                        }}
                      />
                    </Caption>
                  ) : (
                    <Caption inline emphasis="medium">
                      {'\u00a0'}
                    </Caption>
                  )}
                </>
              ) : (
                <>
                  <H6>{consolidation.title}</H6>
                  {stops.length > 1 ? (
                    <div>
                      <Caption inline emphasis="medium">
                        <LocalizedRichText
                          translationKey="dweb_gig_history_value1_value2_value3_value4"
                          params={{
                            value1: stops[0].location.city,
                            value2: stops[0].location.state,
                            value3: stops[stops.length - 1].location.city,
                            value4: stops[stops.length - 1].location.state,
                          }}
                        />
                      </Caption>
                      <Caption inline emphasis="low">
                        <NoBreak>
                          <LocalizedText
                            translationKey="dweb_common_distance_miles"
                            params={{ distance: Math.round(consolidation.estimated_distance) }}
                          />
                        </NoBreak>
                      </Caption>
                    </div>
                  ) : stops.length === 1 ? (
                    <div>
                      <Caption inline emphasis="medium">
                        <LocalizedRichText
                          translationKey="dweb_gig_history_delivery_value1_value2"
                          params={{ value1: stops[0].location.city, value2: stops[0].location.state }}
                        />
                      </Caption>{' '}
                      <Caption inline emphasis="low">
                        <NoBreak>
                          <LocalizedText
                            translationKey="dweb_common_distance_miles"
                            params={{ distance: Math.round(consolidation.estimated_distance) }}
                          />
                        </NoBreak>
                      </Caption>
                    </div>
                  ) : null}
                  {supportIds.length ? (
                    <div>
                      <Caption inline emphasis="medium">
                        <LocalizedRichText
                          translationKey="dweb_gig_history_support_id_value1_value2"
                          params={{ value1: supportIds.length > 1 ? 's' : '', value2: supportIds.join(', ') }}
                        />
                      </Caption>
                    </div>
                  ) : null}
                </>
              )
            }
          </FeatureFlag>
        </TransactionDetails>
      )
    }
  }
}

const Transaction = (props) => {
  const { transaction, consolidation } = props
  const { toLocalizedTime } = useLocalization()

  const date = toLocalizedTime(transaction.created_at)

  const unTappableTypes = [
    'promotional_credit',
    'pending_credit',
    'reconciliation',
    'prop22_earnings_adjustment',
    'seattle_premium',
    'seattle_psst',
    'nyc_earnings_adjustment',
  ]

  const isLink =
    (!unTappableTypes.includes(transaction.category) &&
      (transaction.block_id || (consolidation && consolidation.state !== 'assigned'))) ||
    (transaction.category === 'reconciliation' && transaction.block_id)

  const linkPath = transaction.block_id
    ? `/block-details/${transaction.block_id}`
    : consolidation
      ? `/delivery/${consolidation.id}`
      : ''

  return (
    <TransactionLinkWrapper to={isLink ? linkPath : ''} isLink={isLink}>
      {transaction.status === 'returned' && <TransactionReturnIndicator />}
      {transaction.category === 'pending_credit' ? null : (
        <TransactionDate>
          <Caption emphasis="medium">{date.format({ month: 'short' })}</Caption>
          <H6>{date.format({ day: '2-digit' })}</H6>
          <Caption emphasis="medium">{date.format({ weekday: 'short' })}</Caption>
        </TransactionDate>
      )}
      <TransactionSection transaction={transaction} consolidation={consolidation} />
      <TransactionAmount>
        <H4 id={`transaction-amount-${transaction.id}`} className="transaction-amount" weight="normal">
          <span>$</span>
          {transaction.amount.toFixed(2).split('.')[0]}
          <span>{transaction.amount.toFixed(2).split('.')[1]}</span>
        </H4>
      </TransactionAmount>
    </TransactionLinkWrapper>
  )
}

const LoaderWrapper = styled(TransactionWrapper)`
  justify-content: center;
`
const LoadingTransaction = () => {
  return (
    <LoaderWrapper>
      <Loader size="58" />
    </LoaderWrapper>
  )
}

const PayoutTransactions = styled.div`
  > * {
    border-top: 1px solid ${(props) => props.theme.colors.greyLight};
  }
`
const PayoutWrapper = styled(Panel).attrs({ noPadding: true, left: true })`
  overflow: hidden;
`
const PayoutList = styled.div`
  > ${PayoutWrapper}:not(:first-child) {
    margin-top: 1rem;
  }
`

const PayoutToggleIcon = styled(RoadieUIIcon.ChevronRight)`
  transform: rotate(${(props) => (props.open ? 90 : 0)}deg);
  transition: all 0.3s;
  cursor: pointer;
  width: 100%;
  height: 2rem;
`

const PayoutSummary = styled.div`
  flex: 1 1 100%;
  text-align: center;
  padding: 0 1rem;
`
const PayoutSummaries = styled.div`
  display: flex;
  justify-content: center;

  > :not(:first-child) {
    border-left: 1px solid ${(props) => props.theme.colors.greyLight};
  }
`

const EmptyBalance = () => {
  const staticTextMap = useGigHistoryText()

  return (
    <PayoutWrapper>
      <TransactionWrapper>
        <TransactionDetails>
          <H6>{staticTextMap.CurrentBalance}</H6>
          <Caption emphasis="medium">{staticTextMap.AutomaticPayment}</Caption>
        </TransactionDetails>
        <TransactionAmount>
          <H4>
            <span>$</span>0<span>00</span>
          </H4>
        </TransactionAmount>
      </TransactionWrapper>
    </PayoutWrapper>
  )
}

const Payout = (props) => {
  const { payout, isBalance = false } = props
  const staticTextMap = useGigHistoryText()
  const { formatDate } = useLocalization()
  const [collapsed, setCollapsed] = useState(!isBalance)
  const [consolidations, setConsolidations] = useState(null)

  useEffect(() => {
    if (consolidations) {
      return
    }

    if (!payout.consolidation_ids.length) {
      setConsolidations([])
    } else if (!collapsed) {
      // Get consolidation IDs from regular transactions only
      const regularTransactions = payout.transactions.filter((t) => !t.block_id)
      const regularConsolidationIds = regularTransactions.map((t) => t.consolidation_id).filter((id) => id) // Filter out any undefined/null IDs

      if (regularConsolidationIds.length === 0) {
        setConsolidations([])
      } else {
        fetchConsolidations(regularConsolidationIds).then((cs) => setConsolidations(cs))
      }
    }
  }, [collapsed, consolidations, payout.consolidation_ids, payout.transactions])

  const handleToggle = () => {
    setCollapsed((current) => !current)
  }

  const [fee, regular] = partition(payout.transactions, (t) => t.category === 'instant_pay_fee')

  // TODO if this is the submitted payout (i believe you can check for !isBalance for this)
  // then sum and show the total fees
  console.log('fees', { fee, regular }) // for lint

  return (
    <PayoutWrapper>
      <TransactionWrapper onClick={handleToggle} pointer={true}>
        <TransactionDate>
          <PayoutToggleIcon
            id={`payout-toggle-icon-${payout.payout_id}`}
            className="payout-toggle-icon"
            open={!collapsed}
            $color="primary-base"
          />
        </TransactionDate>
        {isBalance ? (
          <TransactionDetails>
            <H6>{staticTextMap.CurrentBalance}</H6>
            <Caption emphasis="medium">{staticTextMap.AutomaticPayment}</Caption>
          </TransactionDetails>
        ) : (
          <TransactionDetails>
            <H6>
              {formatDate(payout.updated_at, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </H6>
            <Caption id={`payout-type-${payout.payout_id}`} className="payout-type" emphasis="medium">
              {payout.account_type === 'instant'
                ? staticTextMap.Instant
                : payout.account_type === 'payments_service'
                  ? staticTextMap.Automatic
                  : staticTextMap.Automatic}{' '}
              {staticTextMap.Payment}
              {payout.summaries.fees ? ` ($${Math.abs(payout.summaries.fees).toFixed(2)} ${staticTextMap.Fee})` : ''}
            </Caption>
            <Caption
              id={`payout-account-number-text-${payout.payout_id}`}
              className="payout-account-number-text"
              emphasis="low"
            >
              {staticTextMap.PaidTo} {payout.masked_account_number.substr(-3)}
            </Caption>
          </TransactionDetails>
        )}
        <TransactionAmount>
          <H4 id={`payout-total-${payout.payout_id}`} className="payout-total">
            <span>$</span>
            {payout.summaries.total.toFixed(2).split('.')[0]}
            <span>{payout.summaries.total.toFixed(2).split('.')[1]}</span>
          </H4>
        </TransactionAmount>
      </TransactionWrapper>

      {collapsed ? null : (
        <PayoutSummaries>
          <PayoutSummary>
            <SummaryAmount
              id={`payout-gig-pay-value-${payout.payout_id}`}
              className="payout-gig-pay-value"
              amount={payout.summaries.gig_pay}
            />
            <Overline id={`payout-gig-pay-label-${payout.payout_id}`} className="payout-gig-pay-label" emphasis="low">
              {staticTextMap.GigPay}
            </Overline>
          </PayoutSummary>
          <PayoutSummary>
            <SummaryAmount
              id={`payout-expenses-value-${payout.payout_id}`}
              className="payout-expenses-value"
              amount={payout.summaries.reimbursement}
            />
            <Overline id={`payout-expenses-label-${payout.payout_id}`} className="payout-expenses-label" emphasis="low">
              {staticTextMap.Expenses}
            </Overline>
          </PayoutSummary>
          <PayoutSummary>
            <SummaryAmount
              id={`payout-tips-value-${payout.payout_id}`}
              className="payout-tips-value"
              amount={payout.summaries.tip}
            />
            <Overline id={`payout-tips-label-${payout.payout_id}`} className="payout-tips-label" emphasis="low">
              {staticTextMap.Tips}
            </Overline>
          </PayoutSummary>
        </PayoutSummaries>
      )}

      {collapsed ? null : <Spacer height="3.2rem" />}

      {collapsed ? null : consolidations ? (
        <PayoutTransactions>
          {regular
            .sort((a, b) => {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })
            .reverse()
            .map((transaction) => (
              <Transaction
                key={transaction.id}
                transaction={transaction}
                consolidation={
                  transaction.block_id ? null : consolidations.find((c) => c.id === transaction.consolidation_id)
                }
              />
            ))}
        </PayoutTransactions>
      ) : (
        <PayoutTransactions>
          {payout.transactions.map((transaction) => (
            <LoadingTransaction key={transaction.id} />
          ))}
        </PayoutTransactions>
      )}
    </PayoutWrapper>
  )
}

const ProcessingTransactionsWrapper = styled.div`
  > * {
    background: ${(props) => props.theme.colors.white};
  }
  > :not(:first-child) {
    margin-top: ${(props) => (props.returned ? '0' : '1rem')};
  }
`
const ProcessingTransactionsPanel = styled(Panel).attrs({ noPadding: true, left: true })`
  ${(props) =>
    props.returned &&
    css`
      border-top: none;
      :not(:last-child) {
        border-radius: 0;
      }
      :last-child {
        border-radius: 0 0 5px 5px;
      }
    `}
`
const ProcessingTransactions = (props) => {
  const { payout, returned } = props
  const staticTextMap = useGigHistoryText()

  const [collapsed, setCollapsed] = useState(!returned) // Don't collapse returned payments
  const [consolidations, setConsolidations] = useState(null)

  useEffect(() => {
    if (consolidations) {
      return
    }

    if (!payout.consolidation_ids?.length) {
      setConsolidations([])
    } else {
      fetchConsolidations(payout.consolidation_ids).then((cs) => setConsolidations(cs))
    }
  }, [consolidations, payout.consolidation_ids, payout.payout_id])

  const handleToggle = () => {
    setCollapsed((current) => !current)
  }

  const [fee, regular] = partition(payout.transactions, (t) => t.category === 'instant_pay_fee')
  const transactions = regular.slice(0, collapsed ? 5 : regular.length)
  console.log(returned ? 'returned' : 'processing', 'fees', { fee, regular }) // for lint
  const hasGuaranteedTipTransaction = hasGuaranteedTip(transactions)

  return (
    <ProcessingTransactionsWrapper returned={returned}>
      {transactions.map((transaction) => (
        <ProcessingTransactionsPanel returned={returned} key={transaction.id}>
          {consolidations ? (
            <Transaction
              transaction={transaction}
              consolidation={consolidations.find((c) => c.id === transaction.consolidation_id)}
              hasGmt={hasGuaranteedTipTransaction}
            />
          ) : (
            <LoadingTransaction />
          )}
        </ProcessingTransactionsPanel>
      ))}
      {collapsed && payout.transactions.length > transactions.length && (
        <BasicButton mode="secondary" onClick={handleToggle}>
          {staticTextMap.ShowAll}
        </BasicButton>
      )}
    </ProcessingTransactionsWrapper>
  )
}

const ReturnedTransactions = (props) => <ProcessingTransactions {...props} returned={true} />
const ReturnedTransactionsHeader = styled(Panel).attrs({ noPadding: true })`
  border-radius: 5px 5px 0 0;
  padding: 1.6rem;

  ${media.mobile`
    padding: 1.6rem;
  `}
`

const Payouts = () => {
  const [lastPageLoaded, setLastPageLoaded] = useState(null)
  const [payouts, setPayouts] = useState()
  const [hasMore, setHasMore] = useState(true)
  const profile = useSelector((state) => state.profile)
  const creditBalance = useSelector((state) => state.creditBalance?.credits || 0)

  const staticTextMap = useGigHistoryText()

  useEffect(() => {
    fetchPayoutGroups(1, profile.id)
      .then((payouts) => {
        // Add credit balance as a processing transaction if it exists
        if (creditBalance > 0) {
          const creditTransaction = {
            id: 'credit-balance',
            status: 'held',
            category: 'pending_credit',
            amount: creditBalance,
          }

          const creditPayout = {
            payout_id: 'credit-balance',
            transactions: [creditTransaction],
            consolidation_ids: [],
          }

          payouts.processing = [creditPayout, ...payouts.processing]
        }

        setPayouts(payouts)
        setLastPageLoaded(1)
        if (!payouts.processing.length && !payouts.balance.length && !payouts.submitted.length) {
          setHasMore(false)
        }
      })
      .catch(() => {
        // Set empty payouts on error to prevent loading state forever
        setPayouts({
          processing: [],
          balance: [],
          submitted: [],
          returned: [],
        })
        setHasMore(false)
        setLastPageLoaded(1)
      })
  }, [profile.id, creditBalance])

  const loadNextPage = useCallback(() => {
    const nextPage = lastPageLoaded + 1
    fetchPayoutGroups(nextPage, profile.id)
      .then((next) => {
        if (next.processing.length || next.balance.length || next.submitted.length) {
          setPayouts((prev) => ({
            processing: [...prev.processing, ...next.processing],
            balance: [...prev.balance, ...next.balance],
            submitted: [...prev.submitted, ...next.submitted],
            returned: [...prev.returned, ...next.returned],
          }))
        } else {
          setHasMore(false)
        }
        setLastPageLoaded(nextPage)
      })
      .catch(() => {
        // Still update lastPageLoaded to prevent infinite retry loops
        setLastPageLoaded(nextPage)
        // If this was the first page that failed, stop trying
        if (nextPage === 1) {
          setHasMore(false)
        }
      })
  }, [lastPageLoaded, profile.id])

  if (!payouts) {
    return (
      <LoaderWrapper>
        <Loader size="70" />
      </LoaderWrapper>
    )
  }

  const hasReturns = payouts.returned.length

  return (
    <>
      {payouts.processing.length ? (
        <>
          <PanelTitle>{staticTextMap.Processing}</PanelTitle>
          <PayoutList>
            {payouts.processing.map((payout) => (
              <ProcessingTransactions key={payout.payout_id} payout={payout} />
            ))}
          </PayoutList>
          <Spacer height=".8rem" />
          <Caption align="center" emphasis="medium">
            {(profile.certifications || []).includes('trusted_driver')
              ? staticTextMap.ProcessingCaptionTrusted
              : staticTextMap.ProcessingCaptionNotTrusted}
          </Caption>
          <PanelSpacer />
        </>
      ) : null}

      {hasReturns ? (
        <>
          <PaymentError>
            <Caption color="greyDarker" align="center">
              {staticTextMap.PaymentErrorCaption}
            </Caption>
          </PaymentError>
          <PanelSpacer />
        </>
      ) : null}

      <>
        <PanelTitle>{staticTextMap.Balance}</PanelTitle>
        {payouts.balance.length ? (
          <PayoutList>
            {payouts.balance.map((payout) => (
              <Payout isBalance={true} key={payout.payout_id} payout={payout} />
            ))}
          </PayoutList>
        ) : (
          <EmptyBalance />
        )}
        {payouts.returned.length ? (
          <>
            <Spacer height="1rem" />
            <ReturnedTransactionsHeader>
              <Caption emphasis="medium">{staticTextMap.ReturnedTransactionsCaption}</Caption>
            </ReturnedTransactionsHeader>
            <PayoutList>
              {payouts.returned.map((payout) => (
                <ReturnedTransactions key={payout.payout_id} payout={payout} />
              ))}
            </PayoutList>
          </>
        ) : null}
      </>

      {payouts.submitted.length ? (
        <>
          <PanelSpacer />
          <PanelTitle>{staticTextMap.Submitted}</PanelTitle>
          <PayoutList>
            {payouts.submitted.map((payout) => (
              <Payout key={payout.payout_id} payout={payout} />
            ))}
          </PayoutList>
        </>
      ) : null}

      <Spacer height="1rem" />

      {hasMore ? (
        <BasicButton mode="secondary" onClick={loadNextPage}>
          {staticTextMap.ShowMore}
        </BasicButton>
      ) : null}
    </>
  )
}

function timeframeMonths(formatDateTime) {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  const options = []
  for (let i = month; i >= 0; i--) {
    const label = formatDateTime(new Date(year, i), { month: 'short', year: 'numeric' })
    options.push(
      <option key={label} value={`${year}/${i}`}>
        {label}
      </option>
    )
  }
  return options
}

const SummaryTimeframe = styled(Form)`
  display: flex;
  align-items: center;
  justify-content: center;

  & > :first-child {
    min-width: 10rem;
  }
`

const SummaryLabel = styled.label`
  ${(props) => body1(props.theme.colors.black, 'medium')}
  margin-left: 1.2rem;
`

const SummaryItemValue = styled.div`
  ${(props) => (props.small ? h5(props.theme.colors.black) : h2(props.theme.colors.black))}

  ${(props) =>
    props.isCurrency &&
    css`
      &::before {
        content: '$';
        font-size: 0.5em;
        vertical-align: super;
      }
    `}
`

const SummaryItemTitle = styled.div`
  ${(props) => body2(props.theme.colors.black, 'medium')}
`

export const SummaryItem = ({ small, isCurrency, id, title, value, tooltip, ...props }) => (
  <div {...props}>
    <SummaryItemValue id={id} small={small} isCurrency={isCurrency}>
      {value}
    </SummaryItemValue>
    <Tooltip content={tooltip}>
      <SummaryItemTitle>{title}</SummaryItemTitle>
    </Tooltip>
  </div>
)

export const SummaryItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2.4rem;
`

const SubItems = styled.div`
  border-left: 1px solid ${(props) => props.theme.colors.greyLight};
  padding-left: 4rem;

  > :not(:first-child) {
    margin-top: 1rem;
  }
`

const SubItem = styled(SummaryItem)`
  text-align: left;
`

const TotalEarnings = styled(SummaryItem)`
  text-align: right;
  margin-right: 4rem;
`

const EarningsDisclaimer = styled(Caption)`
  padding: 1.6rem 9rem 2rem 9rem;

  ${media.mobile`
    padding: 1.6rem 2.4rem;
  `}
`

const StyledPanelPadding = styled(PanelPadding)`
  ${(props) => (props.showEarningsDisclaimer ? 'padding-bottom: 2rem;' : '')}
`

const LinkWrapper = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.greyLight};

  a {
    display: flex;
    justify-content: space-between;
    align-item: center;
    padding: 1.4rem 2.4rem;

    &:active {
      background: ${(props) => props.theme.colors.primaryLighter};
    }
  }

  span:first-child {
    color: ${(props) => props.theme.colors.black};
  }

  span:last-child {
    letter-spacing: 0.125rem;
    font-weight: 500;
    font-size: 1.4rem;
    display: flex;
    align-items: center;
  }
`

const LinkTitle = styled.span`
  font-weight: 400;
  color: ${(props) => props.theme.colors.greyDark};
`

const CheckIfMobile = () => {
  const staticTextMap = useGigHistoryText()
  const { mediaSize } = useSelector((state) => ({
    mediaSize: state.config.mediaSize,
  }))

  return mediaSize === 'mobile' ? <Icon symbol="arrow-right" palette="grey" /> : <span>{staticTextMap.VIEW}</span>
}

const GigHistoryPage = (props) => {
  const { t, formatDateTime } = useLocalization()
  const localizedTranslations = useLocalizedTranslations(GigHistoryTextKeys)
  const localizedStaticText = localizedTranslations.map
  const [loadingSummary, setLoadingSummary] = useState(false)
  const dispatch = useDispatch()

  const accountSettings = useSelector((state) => state.accountSettings)
  const languagePreference = getLatestAccountSetting(accountSettings, 'preferred_language') || 'english'
  const [translationMap, setTranslationMap] = useState(localizedStaticText)
  const translations = useMemo(() => createLocalizedTranslations(GigHistoryTextKeys, translationMap), [translationMap])
  const [loadingTranslation, setLoadingTranslation] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState('english')
  const googleTranslateTargetLanguage = getGoogleTranslateTargetLanguage(languagePreference)
  const shouldOfferGoogleTranslation =
    Boolean(googleTranslateTargetLanguage) && !hasLocalMessagesForLanguagePreference(languagePreference)

  useEffect(() => {
    const handleTranslate = async () => {
      setLoadingTranslation(true)
      if (currentLanguage != 'english') {
        setTranslationMap(localizedStaticText)
        setCurrentLanguage('english')
        setLoadingTranslation(false)
        dispatch({
          type: 'CLEAR_ALL_FLASH',
        })
        return
      } else {
        if (!googleTranslateTargetLanguage) {
          setLoadingTranslation(false)
          return
        }

        try {
          const translatedMap = await translateText(localizedStaticText, googleTranslateTargetLanguage)
          setTranslationMap(translatedMap)
          setCurrentLanguage(languagePreference)
          setLoadingTranslation(false)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
        } catch (error) {
          console.error('Translation failed:', error)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              message: (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_translation_failed_try_again" />
                </Text.Body1>
              ),
              type: 'error',
            },
          })
        }
      }
    }

    if (shouldOfferGoogleTranslation) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          type: 'info',
          message: (
            <Container.Grid
              onClick={handleTranslate}
              $display="flex"
              $textAlign="center"
              $alignItems="center"
              $justifyContent="center"
              $gridGap="1rem"
            >
              <RoadieUIIcon.Translate $color="neutral-white" $width="2.4rem" />
              {loadingTranslation ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_delivery_details_consolidation_details_translating" />
                </Text.Body1>
              ) : currentLanguage === 'english' ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_tap_translate_strong_text1"
                    params={{ strongText1: <strong>{capitalize(languagePreference)}</strong> }}
                  />
                </Text.Body1>
              ) : (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_viewing_strong_text1_tap_revert_english"
                    params={{ strongText1: <strong>{capitalize(currentLanguage)}</strong> }}
                  />
                </Text.Body1>
              )}
            </Container.Grid>
          ),
        },
      })
    }
  }, [
    accountSettings,
    languagePreference,
    dispatch,
    currentLanguage,
    loadingTranslation,
    localizedStaticText,
    googleTranslateTargetLanguage,
    shouldOfferGoogleTranslation,
  ])

  useEffect(() => {
    if (currentLanguage === 'english') setTranslationMap(localizedStaticText)
  }, [currentLanguage, localizedStaticText])

  const loadSummary = useCallback(
    (range) => {
      setLoadingSummary(true)
      dispatch(fetchDrivingSummary(range)).then(() => {
        setLoadingSummary(false)
      })
    },
    [dispatch]
  )

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const handleTimeFrameChange = (event) => {
    const { value } = event.target
    let range
    switch (value) {
      case 'all': {
        range = null
        break
      }
      case 'ytd': {
        const now = new Date()
        const year = now.getFullYear()
        const start = new Date(year, 0, 1).toISOString()
        const end = new Date(year, 12, 1, 0, 0, 0, -1).toISOString()
        range = { start, end }
        break
      }
      default: {
        const [year, month] = value.split('/')
        const start = new Date(year, month, 1).toISOString()
        const end = new Date(year, Number(month) + 1, 1, 0, 0, 0, -1).toISOString()
        range = { start, end }
        break
      }
    }

    loadSummary(range)
  }

  const { prop22EarningsStatement, nycEarningsStatement, seattleDriver, summary, mediaSize } = props

  const showEarningsDisclaimer = Boolean(
    (prop22EarningsStatement && prop22EarningsStatement.length) || (nycEarningsStatement && nycEarningsStatement.length)
  )

  return (
    <Page sidebar={<DriveSidebar />}>
      <GigHistoryTextContext.Provider value={translations}>
        <Helmet>
          <title>{t('dweb_layout_drive_sidebar_gig_history')}</title>
        </Helmet>

        <PanelTitle>{translations.Summary}</PanelTitle>

        <Panel noPadding>
          <StyledPanelPadding showEarningsDisclaimer={showEarningsDisclaimer}>
            <SummaryTimeframe>
              <Input.Select id="summary-timeframe" onChange={handleTimeFrameChange} defaultValue="all">
                <option value="all">{translations.AllTimeLabel}</option>
                <option value="ytd">
                  <LocalizedText translationKey="dweb_common_year_ytd" params={{ year: new Date().getFullYear() }} />
                </option>
                {timeframeMonths(formatDateTime)}
              </Input.Select>
              <SummaryLabel htmlFor="summary-timeframe">{translations.EarningsSummary}</SummaryLabel>
            </SummaryTimeframe>
            {loadingSummary ? (
              <>
                <Spacer height="2.4rem" />
                <Loader size="70" />
              </>
            ) : (
              <>
                {summary && (
                  <SummaryItems>
                    <TotalEarnings
                      isCurrency={mediaSize !== 'mobile'}
                      value={formatNum(summary.total_earnings, {
                        maxLength: 6,
                      })}
                      title={translations.TotalEarnings}
                      id="total_earnings"
                    />
                    <SubItems>
                      <SubItem
                        small
                        value={`${formatNum(summary.completed_gigs, {
                          maxLength: 6,
                        })} ${translations.Deliveries}`}
                        title={translations.Completed}
                        id="gigs_completed"
                      />
                      <FeatureFlag feature="drive_show_miles_driven">
                        {(enabled) =>
                          enabled && (
                            <SubItem
                              small
                              value={`${formatNum(summary.miles_driven, {
                                roundBy: mediaSize === 'mobile' ? 1 : undefined,
                                maxLength: 6,
                              })} ${translations.Miles}`}
                              title={translations.Driven}
                              id="miles_driven"
                            />
                          )
                        }
                      </FeatureFlag>
                    </SubItems>
                  </SummaryItems>
                )}
              </>
            )}
          </StyledPanelPadding>

          {showEarningsDisclaimer && (
            <EarningsDisclaimer emphasis="medium">{translations.EarningsDisclaimer}</EarningsDisclaimer>
          )}

          <LinkWrapper>
            <Link id="tax-info-link" to={'/tax-information'}>
              <LinkTitle>{translations.TaxInformation}</LinkTitle>
              <CheckIfMobile />
            </Link>
          </LinkWrapper>
          {seattleDriver ? (
            <LinkWrapper>
              <Link id="seattle-dashboard-link" to={'/seattle-dashboard'}>
                <LinkTitle>{translations.SeattleDashboard}</LinkTitle>
                <CheckIfMobile />
              </Link>
            </LinkWrapper>
          ) : null}

          {showEarningsDisclaimer && (
            <LinkWrapper>
              <Link id="earnings-statement-link" to={'/earnings-statement'}>
                <LinkTitle>{translations.EarningsStatements}</LinkTitle>
                <CheckIfMobile />
              </Link>
            </LinkWrapper>
          )}
        </Panel>
        <PanelSpacer />

        <Payouts />
      </GigHistoryTextContext.Provider>
    </Page>
  )
}

export default connect(gigHistorySelector)(GigHistoryPage)
